import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from '../utils/api';
import { Heart, MessageCircle, Star, Share2, Trash2, User, Users, Award, Clock, MapPin, Calendar, Camera, Navigation, Check, ThumbsUp, Search, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCommunityAuth } from '../context/CommunityAuthContext';
import { API } from '../utils/api';
import CommunityBottomNav from '../components/community/CommunityBottomNav';
import CommunityTopHeader from '../components/community/CommunityTopHeader';
import CommunityActionModal from '../components/community/CommunityActionModal';
import WallModal from '../components/modals/WallModal';
import { formatDate, getCategory, getImageForChallenge, categoryStyles, TEAL_ACCENT, TEAL_MUTED, ORANGE_ACCENT } from '../utils/homeHelpers';
import notify from '../utils/notify';
import { hasJoinedEvent, eventAttendeeCount, myEventSubmission, myChallengeParticipation, eventSeatsLeft } from '../utils/communityMembership';

const COMM = `${API}/community`;

const CommunityHomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useCommunityAuth();
  const isAdmin = user?.role === 'COMMUNITY_ADMIN';
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const [posts, setPosts] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [myChallenges, setMyChallenges] = useState([]);
  const [events, setEvents] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  // Items from the new `community_actions` collection (announcement / quiz / poll)
  const [actionAnnouncements, setActionAnnouncements] = useState([]);
  const [actionQuizzes, setActionQuizzes] = useState([]);
  const [actionPolls, setActionPolls] = useState([]);
  const [activeAction, setActiveAction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  // Feed interactions
  const [likedPosts, setLikedPosts] = useState(new Set());
  const [joinedActions, setJoinedActions] = useState(new Set());
  const [expandedPosts, setExpandedPosts] = useState(new Set());
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showWallModal, setShowWallModal] = useState(null);
  const [wallData, setWallData] = useState(null);
  const [translations, setTranslations] = useState({});
  const [translating, setTranslating] = useState(new Set());

  // MAD section state
  const [showMyMAD, setShowMyMAD] = useState(false);
  const [madCarouselIndex, setMadCarouselIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [scopeFilter, setScopeFilter] = useState(null);
  const [serverTime, setServerTime] = useState(new Date());

  // MAD section new filters
  const [dayFilter, setDayFilter] = useState('any');
  const [dayDropdownOpen, setDayDropdownOpen] = useState(false);
  const [distFilter, setDistFilter] = useState('any');
  const [distDropdownOpen, setDistDropdownOpen] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [userCoords, setUserCoords] = useState(null);
  const [geoRequested, setGeoRequested] = useState(false);
  const [pickDate, setPickDate] = useState('');

  const getDistanceKm = useCallback((lat1, lng1, lat2, lng2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLng/2)**2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  }, []);

  const requestGeo = useCallback(() => {
    if (geoRequested) return;
    setGeoRequested(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setUserCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {}
      );
    }
  }, [geoRequested]);

  useEffect(() => { fetchData(); }, [location.key, refreshKey]);

  const fetchData = async () => {
    try {
      const [postsRes, challengesRes, myChallengesRes, eventsRes, timeRes, announcementsRes, actAnnRes, actQuizRes, actPollRes] = await Promise.all([
        axios.get(`${COMM}/posts`, authHeaders).catch(() => ({ data: [] })),
        axios.get(`${COMM}/challenges`, authHeaders).catch(() => ({ data: [] })),
        axios.get(`${COMM}/challenges/my`, authHeaders).catch(() => ({ data: [] })),
        axios.get(`${COMM}/events`, authHeaders).catch(() => ({ data: [] })),
        axios.get(`${COMM}/server-time`).catch(() => ({ data: { server_time: new Date().toISOString() } })),
        axios.get(`${COMM}/announcements`, authHeaders).catch(() => ({ data: [] })),
        // New `community_actions` collection — filtered server-side to active window.
        axios.get(`${COMM}/actions?action_type=announcement`, authHeaders).catch(() => ({ data: [] })),
        axios.get(`${COMM}/actions?action_type=quiz`, authHeaders).catch(() => ({ data: [] })),
        axios.get(`${COMM}/actions?action_type=poll`, authHeaders).catch(() => ({ data: [] })),
      ]);
      setPosts(postsRes.data || []);
      setChallenges(challengesRes.data || []);
      setMyChallenges(myChallengesRes.data || []);
      setEvents(eventsRes.data || []);
      setServerTime(new Date(timeRes.data.server_time));
      setAnnouncements(announcementsRes.data || []);
      setActionAnnouncements(actAnnRes.data || []);
      setActionQuizzes(actQuizRes.data || []);
      setActionPolls(actPollRes.data || []);
      // Mark joined challenges from both endpoints so the carousel "Join"
      // button reflects existing memberships even before the user opens the
      // detail page.
      const joined = new Set();
      (challengesRes.data || []).forEach(c => {
        if (myChallengeParticipation(c, user?.id)) joined.add(c.id);
      });
      (myChallengesRes.data || []).forEach(c => joined.add(c.id));
      setJoinedActions(joined);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // === INTERACTION HANDLERS ===
  const handleLikePost = async (postId) => {
    try {
      const response = await axios.post(`${COMM}/posts/${postId}/like`, {}, authHeaders);
      setLikedPosts(prev => { const s = new Set(prev); response.data.liked ? s.add(postId) : s.delete(postId); return s; });
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, likes: response.data.total_likes } : p));
    } catch { console.error('Failed to like post'); }
  };

  const handleDeletePost = async (postId) => {
    try {
      await axios.delete(`${COMM}/posts/${postId}`, authHeaders);
      setPosts(prev => prev.filter(p => p.id !== postId));
      setDeleteConfirm(null);
      setRefreshKey(prev => prev + 1);
    } catch (error) { alert(error.response?.data?.detail || 'Failed to delete post'); }
  };

  const handleJoinChallenge = async (challengeId) => {
    try {
      await axios.post(`${COMM}/challenges/${challengeId}/join`, {}, authHeaders);
      setJoinedActions(prev => new Set([...prev, challengeId]));
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      const detail = error?.response?.data?.detail || '';
      const alreadyJoined = error?.response?.status === 400 && /already joined/i.test(detail);
      const ended = error?.response?.status === 400 && /ended/i.test(detail);
      if (alreadyJoined) {
        setJoinedActions(prev => new Set([...prev, challengeId]));
      } else if (ended) {
        notify.error(detail || 'This action has ended');
        setRefreshKey(prev => prev + 1);
      } else if (detail) {
        notify.error(detail);
      }
    }
  };

  const handleShare = async (item) => {
    // Build a shareable URL pointing to the community post detail page so
    // recipients land on the actual post, not the home feed.
    const postUuid = item?.postId || item?.id;
    const shareUrl = postUuid
      ? `${window.location.origin}/community/posts/${postUuid}`
      : window.location.href;
    const shareData = { title: item.content || item.title, url: shareUrl };
    try {
      if (navigator.share) await navigator.share(shareData);
      else { await navigator.clipboard.writeText(shareData.url); alert('Link copied!'); }
    } catch {}
  };

  const handleTranslate = async (postId, text) => {
    if (translations[postId]) { setTranslations(prev => { const n = { ...prev }; delete n[postId]; return n; }); return; }
    setTranslating(prev => new Set([...prev, postId]));
    try {
      const res = await axios.post(`${COMM}/translate`, { text }, authHeaders);
      setTranslations(prev => ({ ...prev, [postId]: res.data.translated_text }));
    } catch {} finally { setTranslating(prev => { const s = new Set(prev); s.delete(postId); return s; }); }
  };

  const handleRsvp = async (eventId) => {
    try {
      const ev = events.find(e => e.id === eventId || e.eventId === eventId);
      const hasRsvp = ev && hasJoinedEvent(ev, user?.id);
      if (hasRsvp) {
        await axios.delete(`${COMM}/events/${eventId}/rsvp`, authHeaders);
      } else {
        await axios.post(`${COMM}/events/${eventId}/rsvp`, {}, authHeaders);
      }
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      const msg = err.response?.data?.detail;
      if (msg) alert(msg);
    }
  };

  const handleGeoConfirm = async (eventId) => {
    if (!navigator.geolocation) { alert('Geolocation not supported'); return; }
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        await axios.post(`${COMM}/events/${eventId}/submit-completion`,
          { geo_coords: { latitude: pos.coords.latitude, longitude: pos.coords.longitude } }, authHeaders);
        setRefreshKey(prev => prev + 1);
      } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
    }, () => alert('Please allow location access'));
  };

  const handlePhotoConfirm = async (eventId) => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const fd = new FormData(); fd.append('file', file);
      try {
        const upRes = await axios.post(`${COMM}/upload`, fd,
          { headers: { ...authHeaders.headers, 'Content-Type': 'multipart/form-data' } });
        await axios.post(`${COMM}/events/${eventId}/submit-completion`,
          { photo_url: upRes.data.url }, authHeaders);
        setRefreshKey(prev => prev + 1);
      } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
    };
    input.click();
  };

  const handleVote = async (eventId, optionId) => {
    try {
      await axios.post(`${COMM}/events/${eventId}/vote/${optionId}`, {}, authHeaders);
      setRefreshKey(prev => prev + 1);
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  // === FEED CARDS ===
  const feedCards = useMemo(() => {
    const cards = [];
    // Events have been split out of the feed into the Featured Events
    // carousel (top of the page) and the dedicated All Events page. The feed
    // now shows posts only.
    // Posts
    posts.forEach(post => {
      const userName = post.username || post.user_name || 'Community Member';
      const names = userName.split(' ').filter(n => n.length > 0);
      const initials = names.length > 0 ? names.map(n => n[0] || '').join('').toUpperCase().slice(0, 2) : 'CM';
      cards.push({
        type: 'user_post', id: `post_${post.id}`, user: { name: userName, initials },
        timestamp: new Date(post.created_at).toLocaleDateString(),
        content: post.content, hashtags: post.tags?.map(t => `#${t}`) || [],
        image: post.media_url || null, postId: post.id,
        created_at: post.created_at, user_id: post.user_id
      });
    });
    return cards;
  }, [posts]);

  // === MAD SECTION ===
  const filteredChallenges = useMemo(() => {
    const now = new Date();
    // "My MAD" tab is sourced from /community/challenges/my so a freshly
    // joined action surfaces even if it doesn't appear in the global
    // /community/challenges list yet (pagination/approval state etc).
    const universe = showMyMAD ? myChallenges : challenges;
    return universe.filter(challenge => {
      if (scopeFilter) {
        const modeMap = { 'solo': ['solo', 'individual'], 'team': ['team'], 'company': ['company', 'community'] };
        if (!modeMap[scopeFilter]?.includes(challenge.mode?.toLowerCase())) return false;
      }
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        if (!(challenge.title || '').toLowerCase().includes(q) && !(challenge.description || '').toLowerCase().includes(q)) return false;
      }
      // Category filter
      if (categoryFilter !== 'all') {
        const cat = getCategory(challenge.title, challenge.category).toLowerCase().replace(/\s+/g, '').replace('dev', '');
        if (!cat.startsWith(categoryFilter)) return false;
      }
      // Day filter
      if (dayFilter !== 'any' && challenge.start_date) {
        const sd = new Date(challenge.start_date);
        if (dayFilter === 'today') {
          if (sd.toDateString() !== now.toDateString()) return false;
        } else if (dayFilter === 'weekend') {
          const sat = new Date(now); sat.setDate(now.getDate() + (6 - now.getDay()));
          const sun = new Date(sat); sun.setDate(sat.getDate() + 1);
          if (sd.toDateString() !== sat.toDateString() && sd.toDateString() !== sun.toDateString()) return false;
        } else if (dayFilter === 'week') {
          const weekEnd = new Date(now); weekEnd.setDate(now.getDate() + 7);
          if (sd < now || sd > weekEnd) return false;
        } else if (dayFilter === 'pick' && pickDate) {
          if (sd.toDateString() !== new Date(pickDate).toDateString()) return false;
        }
      }
      // Distance filter
      if (distFilter === 'online') {
        if (challenge.event_type !== 'online') return false;
      } else if (distFilter !== 'any' && userCoords && challenge.location_coords) {
        const km = getDistanceKm(userCoords.lat, userCoords.lng, challenge.location_coords.lat, challenge.location_coords.lng);
        if (km > parseInt(distFilter)) return false;
      }
      return true;
    });
  }, [challenges, myChallenges, showMyMAD, scopeFilter, searchQuery, categoryFilter, dayFilter, pickDate, distFilter, userCoords, getDistanceKm]);

  const currentChallenge = filteredChallenges[madCarouselIndex];

  // Highlights: ended challenges user has joined
  const highlights = useMemo(() => {
    const now = new Date();
    return challenges.filter(ch => {
      if (!ch.end_date) return false;
      if (new Date(ch.end_date) >= now) return false;
      return !!myChallengeParticipation(ch, user?.id);
    });
  }, [challenges, user?.id]);

  // Top-of-page event carousel.
  // Always shows up to 3 cards:
  //   1) Upcoming events (start_date >= today), sorted ascending by start_date.
  //   2) If no upcoming events exist, fall back to the 3 most recently ended.
  // The `is_featured` flag is now purely cosmetic — surfaces a ⭐ badge on the card
  // but no longer gates inclusion.
  const featuredEvents = useMemo(() => {
    const todayMs = (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime(); })();
    const startMs = ev => {
      const s = ev.start_date || ev.event_date;
      if (!s) return Number.POSITIVE_INFINITY;
      const t = new Date(s).getTime();
      return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
    };
    const endMs = ev => {
      const e = ev.end_date || ev.event_date || ev.start_date;
      if (!e) return -Number.POSITIVE_INFINITY;
      const t = new Date(e).getTime();
      return Number.isNaN(t) ? -Number.POSITIVE_INFINITY : t;
    };
    const all = events || [];
    const upcoming = all.filter(ev => startMs(ev) >= todayMs)
      .sort((a, b) => startMs(a) - startMs(b))
      .slice(0, 3);
    if (upcoming.length > 0) return upcoming;
    // Fallback — 3 most recently ended events.
    return all.filter(ev => endMs(ev) < todayMs)
      .sort((a, b) => endMs(b) - endMs(a))
      .slice(0, 3);
  }, [events]);
  const [featuredIndex, setFeaturedIndex] = useState(0);

  // Authoritative join check used by the carousel "Join" button.
  // Considers: optimistic local set, /community/challenges/my membership,
  // and the challenge.participants[] list itself. This ensures the
  // correct state shows immediately on page load.
  const myChallengeIds = useMemo(() => new Set(myChallenges.map(c => c.id)), [myChallenges]);
  const isChallengeJoined = (challenge) => {
    if (!challenge) return false;
    if (joinedActions.has(challenge.id)) return true;
    if (myChallengeIds.has(challenge.id)) return true;
    if (!user?.id) return false;
    return !!myChallengeParticipation(challenge, user.id);
  };

  // Category list
  const MAD_CATEGORIES = [
    { id: 'all', label: 'All' },
    { id: 'wellness', label: 'Wellness' },
    { id: 'climate', label: 'Climate' },
    { id: 'community', label: 'Community' },
    { id: 'career', label: 'Career' },
    { id: 'inclusion', label: 'Inclusion' },
    { id: 'other', label: 'Other' },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#F5F7FA' }}>
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }}>
          <Star size={40} fill="#52B8AC" color="#52B8AC" />
        </motion.div>
      </div>
    );
  }

  return (
    <div style={{ background: '#F5F7FA', minHeight: '100vh', paddingBottom: '80px' }}>
      <CommunityTopHeader />
      <div style={{ width: '100%', maxWidth: 380, margin: '0 auto', padding: '0 16px' }}>

        {/* ─────────────────────── Featured Events Carousel ─────────────────────── */}
        <div data-testid="featured-events-section" style={{ padding: '14px 0 4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1F2937', margin: 0 }}>Featured Events</h3>
              <button data-testid="view-all-events-link" type="button" onClick={() => navigate('/community/events')}
                style={{ background: 'none', border: 'none', fontSize: 12, fontWeight: 600, color: TEAL_ACCENT, cursor: 'pointer', padding: 0 }}>
                View all events &rarr;
              </button>
            </div>
            <div data-testid="featured-events-carousel"
              style={{ position: 'relative', display: 'flex', overflowX: 'auto', scrollSnapType: 'x mandatory', scrollBehavior: 'smooth', WebkitOverflowScrolling: 'touch', gap: 0 }}
              className="hide-scrollbar"
              onScroll={(e) => {
                const w = e.currentTarget.clientWidth;
                if (w > 0) setFeaturedIndex(Math.round(e.currentTarget.scrollLeft / w));
              }}>
              {featuredEvents.length > 0 ? featuredEvents.map(ev => {
                const cat = getCategory(ev.title, ev.category);
                const reward = ev.reward_stars || ev.reward_points || 30;
                const isJoined = hasJoinedEvent(ev, user?.id);
                const seatsLeft = eventSeatsLeft(ev);
                const isFull = !isJoined && seatsLeft === 0;
                // Diagnostic — print the full event object so we can verify
                // photo_tag is actually being passed in by the API. Look in
                // browser devtools console for "[featured-event]" entries.
                console.log('[featured-event]', ev.id, { photo_tag: ev.photo_tag, ev });
                return (
                  <div key={ev.id} data-testid={`featured-event-${ev.id}`}
                    style={{ flexShrink: 0, width: '100%', scrollSnapAlign: 'start', boxSizing: 'border-box', padding: '0 2px' }}
                    onClick={() => navigate(`/community/event-detail/${ev.id}`, { state: { event: ev } })}>
                    <div style={{
                      position: 'relative', borderRadius: 20, overflow: 'hidden',
                      height: 220, cursor: 'pointer',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                    }}>
                      {/* Background image (image_url) or teal fallback. The
                          fallback gradient sits beneath the <img>; if the URL
                          is null/empty the <img> never renders, and if it 404s
                          the onError handler hides the <img>. Either way the
                          gradient shows through. Backend URLs can be relative
                          (e.g. /uploads/abc.jpg) — prefix with the backend
                          host so the browser resolves them correctly. */}
                      <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(135deg, ${TEAL_ACCENT}, ${TEAL_MUTED})` }} />
                      {ev.image_url && (
                        <img
                          src={ev.image_url.startsWith('/') ? `${API.replace('/api', '')}${ev.image_url}` : ev.image_url}
                          alt=""
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                      )}
                      {/* Gradient overlay — softer top, deeper bottom for legibility */}
                      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.55) 100%)' }} />
                      {/* Category pill — frosted glass */}
                      <span style={{ position: 'absolute', top: 12, left: 12, padding: '5px 12px', borderRadius: 20, fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', background: 'rgba(255,255,255,0.22)', border: '0.5px solid rgba(255,255,255,0.35)', color: '#FFFFFF', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}>
                        {cat}
                      </span>
                      {/* RSVP button top right */}
                      <button data-testid={`featured-rsvp-${ev.id}`} type="button"
                        disabled={isFull}
                        onClick={(e) => { e.stopPropagation(); if (!isFull) navigate(`/community/event-detail/${ev.id}`, { state: { event: ev } }); }}
                        style={{ position: 'absolute', top: 12, right: 12, padding: '5px 12px', borderRadius: 20, border: 'none',
                          background: isJoined || isFull ? 'rgba(255,255,255,0.85)' : '#FFFFFF', color: isJoined || isFull ? '#94A3B8' : '#1F2937',
                          fontSize: 10, fontWeight: 700, cursor: isFull ? 'default' : 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
                        {isJoined ? 'Joined' : isFull ? 'Full' : 'RSVP'}
                      </button>
                      {/* Reward pill + title + meta — bottom block */}
                      <div style={{ position: 'absolute', left: 14, right: 14, bottom: 14 }}>
                        <span data-testid={`featured-reward-pill-${ev.id}`}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5,
                            background: 'rgba(255,255,255,0.18)', border: '0.5px solid rgba(255,255,255,0.3)',
                            color: '#FFFFFF', fontSize: 10, fontWeight: 600,
                            padding: '3px 9px', borderRadius: 20, marginBottom: 8,
                            backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}>
                          <Star size={10} fill="#FBBF24" color="#FBBF24" /> Earn {reward} stars &middot; Redeem vouchers
                        </span>
                        <div style={{ fontSize: 17, fontWeight: 700, color: '#FFFFFF', lineHeight: 1.3, marginBottom: 6, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textShadow: '0 1px 3px rgba(0,0,0,0.35)' }}>{ev.title}</div>
                        <div style={{ display: 'flex', gap: 12, fontSize: 11, color: 'rgba(255,255,255,0.88)', alignItems: 'center', flexWrap: 'wrap' }}>
                          {ev.event_date && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <Calendar size={12} />{new Date(ev.event_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                            </span>
                          )}
                          {seatsLeft != null && (
                            <span data-testid={`featured-seats-${ev.id}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <Users size={12} />{seatsLeft === 0 ? 'Full' : `${seatsLeft} seats left`}
                            </span>
                          )}
                          {(ev.location || ev.distance_km !== undefined) && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <MapPin size={12} />{ev.distance_km != null ? `${ev.distance_km} km` : (ev.location?.split(',')[0] || 'Nearby')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }) : (
                // No events at all in DB — render 3 skeleton cards so the carousel
                // is never visually empty.
                [0, 1, 2].map(i => (
                  <div key={`skel-${i}`} data-testid={`featured-event-skeleton-${i}`}
                    style={{ flexShrink: 0, width: '100%', scrollSnapAlign: 'start', boxSizing: 'border-box', padding: '0 2px' }}>
                    <div style={{
                      position: 'relative', borderRadius: 20, overflow: 'hidden',
                      height: 220, background: 'linear-gradient(135deg, #E5E7EB, #F3F4F6)',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      color: '#9CA3AF', fontSize: 13, gap: 6,
                    }}>
                      <Calendar size={28} color="#9CA3AF" />
                      <span>No events yet</span>
                    </div>
                  </div>
                ))
              )}
            </div>
            {/* Dots */}
            {featuredEvents.length > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 8 }} data-testid="featured-events-dots">
                {featuredEvents.map((_, i) => (
                  <span key={i} style={{ width: i === featuredIndex ? 18 : 6, height: 6, borderRadius: 3, background: i === featuredIndex ? TEAL_ACCENT : '#D1D5DB', transition: 'width 0.2s ease' }} />
                ))}
              </div>
            )}
          </div>

        {/* ─────────────────────── Make A Difference Shelf ─────────────────────── */}
        <div data-testid="mad-shelf-section" style={{ background: '#EDF7F4', borderRadius: 16, padding: '14px 12px', marginTop: 12 }}>
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#1F2937' }}>Make A Difference</div>
            <div style={{ fontSize: 12, color: '#9CA3AF' }}>Your recent highlights</div>
          </div>
          {(actionAnnouncements.length + actionQuizzes.length + actionPolls.length + highlights.length) > 0 ? (
            <div data-testid="highlights-shelf" style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4, WebkitOverflowScrolling: 'touch' }} className="hide-scrollbar">

              {/* 1. Announcement tiles */}
              {actionAnnouncements.map(a => (
                <div key={`ann-act-${a.id}`} data-testid={`announcement-tile-${a.id}`}
                  onClick={() => setActiveAction(a)}
                  style={{ flexShrink: 0, width: 95, height: 115, borderRadius: 12, overflow: 'hidden', position: 'relative', cursor: 'pointer',
                    background: a.image_url ? `url(${a.image_url}) center/cover` : 'linear-gradient(135deg, #5DBFAD, #7DD3C0)' }}>
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 55%)' }} />
                  <span style={{ position: 'absolute', top: 6, left: 6, fontSize: 9, fontWeight: 700, color: '#FFF', background: 'rgba(0,0,0,0.55)', padding: '2px 6px', borderRadius: 8, letterSpacing: 0.4 }}>ANNOUNCEMENT</span>
                  <div style={{ position: 'absolute', bottom: 6, left: 6, right: 6 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#fff', lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{a.title}</div>
                  </div>
                </div>
              ))}

              {/* 2. Quiz tiles */}
              {actionQuizzes.map(q => (
                <div key={`quiz-${q.id}`} data-testid={`quiz-tile-${q.id}`}
                  onClick={() => setActiveAction(q)}
                  style={{ flexShrink: 0, width: 95, height: 115, borderRadius: 12, overflow: 'hidden', position: 'relative', cursor: 'pointer',
                    background: q.image_url ? `url(${q.image_url}) center/cover` : 'linear-gradient(135deg, #8B5CF6, #C084FC)' }}>
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.78) 0%, transparent 55%)' }} />
                  <span style={{ position: 'absolute', top: 6, left: 6, fontSize: 9, fontWeight: 700, color: '#FFF', background: 'rgba(139,92,246,0.85)', padding: '2px 6px', borderRadius: 8, letterSpacing: 0.4 }}>QUIZ</span>
                  {!q.my_response && (
                    <span style={{ position: 'absolute', top: 6, right: 6, fontSize: 9, fontWeight: 700, color: '#92400E', background: '#FEF3C7', padding: '2px 6px', borderRadius: 8, display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                      +5 {'\u2B50'}
                    </span>
                  )}
                  <div style={{ position: 'absolute', bottom: 6, left: 6, right: 6 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#fff', lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{q.question || q.title}</div>
                  </div>
                </div>
              ))}

              {/* 3. Poll tiles */}
              {actionPolls.map(p => (
                <div key={`poll-${p.id}`} data-testid={`poll-tile-${p.id}`}
                  onClick={() => setActiveAction(p)}
                  style={{ flexShrink: 0, width: 95, height: 115, borderRadius: 12, overflow: 'hidden', position: 'relative', cursor: 'pointer',
                    background: p.image_url ? `url(${p.image_url}) center/cover` : 'linear-gradient(135deg, #F59E0B, #FBBF24)' }}>
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.78) 0%, transparent 55%)' }} />
                  <span style={{ position: 'absolute', top: 6, left: 6, fontSize: 9, fontWeight: 700, color: '#FFF', background: 'rgba(245,158,11,0.95)', padding: '2px 6px', borderRadius: 8, letterSpacing: 0.4 }}>POLL</span>
                  {!p.my_response && (
                    <span style={{ position: 'absolute', top: 6, right: 6, fontSize: 9, fontWeight: 700, color: '#92400E', background: '#FEF3C7', padding: '2px 6px', borderRadius: 8, display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                      +5 {'\u2B50'}
                    </span>
                  )}
                  <div style={{ position: 'absolute', bottom: 6, left: 6, right: 6 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#fff', lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{p.question || p.title}</div>
                  </div>
                </div>
              ))}

              {/* 4. Existing event memories */}
              {highlights.map(ch => (
                <div key={ch.id} onClick={() => {
                    const photos = ch.highlight_photos?.length ? ch.highlight_photos : (ch.photo_tag ? [ch.photo_tag] : []);
                    setWallData({ memory: { event_title: ch.title, event_date: ch.end_date, images: photos }, messages: [] });
                    setShowWallModal({ type: 'event_memory' });
                  }}
                  style={{ flexShrink: 0, width: 95, height: 115, borderRadius: 12, overflow: 'hidden', position: 'relative', cursor: 'pointer',
                    background: ch.photo_tag ? `url(${ch.photo_tag}) center/cover` : `linear-gradient(135deg, ${TEAL_ACCENT}, ${TEAL_MUTED})` }}>
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 55%)' }} />
                  <div style={{ position: 'absolute', bottom: 6, left: 6, right: 6 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#fff', lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{ch.title}</div>
                    <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>{new Date(ch.end_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: '#9CA3AF', textAlign: 'center', padding: '14px 0' }}>Nothing here yet — check back soon.</div>
          )}
        </div>

        {/* ─────────────────────── Create Post Nudge ─────────────────────── */}
        <div data-testid="create-post-nudge"
          onClick={() => navigate('/community/create-post')}
          style={{
            display: 'flex', alignItems: 'center', gap: 12,
            marginTop: 12, padding: '12px 14px',
            background: '#FFFFFF', borderRadius: 14,
            border: '1px solid rgba(0,0,0,0.06)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            cursor: 'pointer',
          }}>
          <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#F3F4F6', border: `2px solid ${TEAL_ACCENT}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: TEAL_ACCENT, fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
            {(user?.username || 'CM').split(' ').map(n => n[0] || '').join('').toUpperCase().slice(0, 2)}
          </div>
          <span style={{ flex: 1, fontSize: 13, color: '#9CA3AF' }}>Share something with the community…</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, color: '#92400E', background: '#FEF3C7', padding: '4px 8px', borderRadius: 10 }}>
            +3 <Star size={11} fill="#F59E0B" color="#F59E0B" />
          </span>
        </div>

        {/* WE Feed — vertical stack. Each card sizes to its own content
            (text-only posts shrink to text height; posts with images grow
            to fit the image). No fixed height, no horizontal swipe. */}
        <div style={{ padding: '12px 0 16px' }}>
          <div style={{ position: 'relative', marginBottom: 0 }}>
            <div data-testid="we-feed-carousel"
              style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {feedCards.map((card, cardIndex) => {
                const config = {
                  user_post: { accent: TEAL_MUTED, label: 'User Post', headerBg: '#FFFFFF', headerText: TEAL_ACCENT },
                  event: { accent: TEAL_ACCENT, label: card.event_type === 'voting' ? 'Community Poll' : 'Community Event', headerBg: TEAL_ACCENT, headerText: '#FFFFFF' }
                }[card.type] || { accent: TEAL_MUTED, label: 'Post', headerBg: '#FFFFFF', headerText: TEAL_ACCENT };

                return (
                  <div key={card.id} data-testid={`carousel-card-${cardIndex}`}
                    style={{ width: '100%' }}>
                    <div style={{ background: '#FFFFFF', borderRadius: 20, overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.06)' }}>
                      {/* Header */}
                      <div style={{ background: config.headerBg, padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                        <span style={{ fontSize: 10, fontWeight: 600, color: config.headerText, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{config.label}</span>
                        {card.type === 'event' && <span style={{ fontSize: 11, fontWeight: 500, color: '#FFFFFF', border: '1px solid rgba(255,255,255,0.4)', padding: '4px 10px', borderRadius: 6 }}>
                          {card.event_type === 'voting' ? 'Vote Now' : 'RSVP'}
                        </span>}
                      </div>
                      {/* Content */}
                      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column' }}>
                        {/* User Post */}
                        {card.type === 'user_post' && (
                          <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
                              <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#F3F4F6', border: `2px solid ${TEAL_ACCENT}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: TEAL_ACCENT, fontWeight: 600, fontSize: 14 }}>
                                {card.user?.initials || 'U'}
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 14, fontWeight: 600, color: '#1F2937' }}>{card.user?.name || 'Community Member'}</div>
                                <div style={{ fontSize: 11, color: '#9CA3AF' }}>{formatDate(card.created_at)}</div>
                              </div>
                              {/* "Earned 3 stars" badge — every post earns 3 stars when published */}
                              <span data-testid={`earned-stars-${card.postId}`}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                                  fontSize: 11, fontWeight: 700, color: '#92400E', background: '#FEF3C7',
                                  padding: '4px 8px', borderRadius: 10, whiteSpace: 'nowrap' }}>
                                <Star size={11} fill="#F59E0B" color="#F59E0B" /> Earned 3
                              </span>
                              {(card.user_id === user?.id || isAdmin) && (
                                <button onClick={(e) => { e.stopPropagation(); setDeleteConfirm({ type: 'post', id: card.postId }); }}
                                  style={{ padding: 8, background: 'transparent', border: 'none', borderRadius: 8, cursor: 'pointer', color: '#9CA3AF' }}><Trash2 size={16} /></button>
                              )}
                            </div>
                            <div style={{ marginBottom: 8 }}>
                              <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.5, margin: 0, display: expandedPosts.has(card.id) ? 'block' : '-webkit-box', WebkitLineClamp: expandedPosts.has(card.id) ? 'none' : 2, WebkitBoxOrient: 'vertical', overflow: expandedPosts.has(card.id) ? 'visible' : 'hidden' }}>{translations[card.postId] || card.content}</p>
                              <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
                                {card.content?.length > 80 && (
                                  <button onClick={(e) => { e.stopPropagation(); setExpandedPosts(prev => { const s = new Set(prev); s.has(card.id) ? s.delete(card.id) : s.add(card.id); return s; }); }}
                                    style={{ background: 'none', border: 'none', padding: 0, fontSize: 12, color: TEAL_ACCENT, fontWeight: 500, cursor: 'pointer' }}>
                                    {expandedPosts.has(card.id) ? 'Show less' : 'Read more'}
                                  </button>
                                )}
                                <button data-testid={`translate-${card.postId}`} onClick={(e) => { e.stopPropagation(); handleTranslate(card.postId, card.content); }}
                                  disabled={translating.has(card.postId)}
                                  style={{ background: 'none', border: 'none', padding: 0, fontSize: 12, color: '#6B7280', fontWeight: 500, cursor: 'pointer' }}>
                                  {translating.has(card.postId) ? 'Translating...' : translations[card.postId] ? 'Show original' : 'Translate'}
                                </button>
                              </div>
                            </div>
                            {card.hashtags?.length > 0 && (
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                                {card.hashtags.slice(0, 3).map((tag, i) => <span key={i} style={{ color: TEAL_ACCENT, fontSize: 11, fontWeight: 500 }}>{tag}</span>)}
                              </div>
                            )}
                            {card.image && (
                              <div style={{ borderRadius: 8, overflow: 'hidden', background: '#F9FAFB' }}>
                                <img src={card.image.startsWith('/') ? `${API.replace('/api', '')}${card.image}` : card.image} alt=""
                                  onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                                  style={{ width: '100%', height: 'auto', display: 'block' }} />
                              </div>
                            )}
                          </>
                        )}

                        {/* Event Card */}
                        {card.type === 'event' && (
                          <div onClick={() => navigate(`/community/event-detail/${card.eventId}`, { state: { event: card } })}
                            style={{ display: 'flex', flexDirection: 'column', maxHeight: 520, overflow: 'hidden', cursor: 'pointer' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                              <div style={{ width: 48, height: 48, borderRadius: 14, background: `linear-gradient(135deg, ${TEAL_ACCENT}15, ${TEAL_ACCENT}08)`, border: `2px solid ${TEAL_ACCENT}25`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                {card.event_type === 'voting' ? <ThumbsUp size={24} color={TEAL_ACCENT} /> : <Calendar size={24} color={TEAL_ACCENT} />}
                              </div>
                              <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1F2937', margin: 0, lineHeight: 1.3, flex: 1 }}>{card.title}</h3>
                              {card.event_type !== 'voting' && myEventSubmission(card, user?.id)?.status === 'approved' && (
                                <div data-testid="status-approved-top" style={{ width: 20, height: 20, borderRadius: '50%', background: TEAL_ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                  <Check size={12} color="#fff" strokeWidth={3} />
                                </div>
                              )}
                            </div>
                            <p style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.6, marginBottom: 8, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{card.description}</p>
                            {card.location && card.eventMode !== 'online' && (
                              <a href={`https://maps.google.com/?q=${encodeURIComponent(card.location)}`}
                                target="_blank" rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                style={{ fontSize: 12, color: TEAL_ACCENT, display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4, textDecoration: 'none', fontWeight: 500 }}>
                                <MapPin size={12} /> {card.location}
                              </a>
                            )}
                            {card.eventMode === 'online' && card.meetLink && (
                              <a href={card.meetLink} target="_blank" rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`join-online-${card.eventId}`}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: TEAL_ACCENT, background: `${TEAL_ACCENT}12`, padding: '6px 12px', borderRadius: 8, textDecoration: 'none', marginBottom: 4 }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 10l5-5m0 0v4m0-4h-4"/><path d="M4 20l5-5"/><path d="M15 4h5v5"/><rect x="2" y="14" width="8" height="8" rx="1"/></svg>
                                Join Online
                              </a>
                            )}
                            {card.event_date && (
                              <div style={{ fontSize: 12, color: '#6B7280', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 8 }}>
                                <Calendar size={12} /> {new Date(card.event_date).toLocaleDateString()}
                                {card.frequency && card.frequency !== 'once' && (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, marginLeft: 10, color: '#6B7280' }}>
                                    <Clock size={12} /> {card.frequency === 'weekly' ? 'Weekly' : card.frequency === 'monthly' ? 'Monthly' : card.frequency}
                                  </span>
                                )}
                              </div>
                            )}

                            {/* Cover Image */}
                            {(card.image_url || (card.photo_tag && card.photo_tag.trim() !== '')) && (
                              <img src={card.image_url || card.photo_tag} alt=""
                                onError={(e) => { e.target.style.display = 'none'; }}
                                style={{ width: '100%', height: 180, objectFit: 'cover', borderRadius: 12, marginBottom: 10 }} />
                            )}

                            {/* POC + Attendees row
                            {card.event_type !== 'voting' && (card.pocName || eventAttendeeCount(card) > 0) && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, fontSize: 12, color: '#6B7280' }}>
                                {card.pocName && (
                                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <User size={12} /> {card.pocName}
                                  </span>
                                )}
                                {eventAttendeeCount(card) > 0 && (
                                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <Users size={12} /> {eventAttendeeCount(card)} attending
                                  </span>
                                )}
                              </div>
                            )} */}

                            {/* Voting Options */}
                            {card.event_type === 'voting' && card.options?.length > 0 && (
                              <div style={{ marginBottom: 12 }}>
                                {card.options.map((opt) => {
                                  const totalVotes = card.options.reduce((s, o) => s + (o.votes?.length || 0), 0);
                                  const voted = opt.votes?.includes(user?.id);
                                  const pct = totalVotes > 0 ? Math.round(((opt.votes?.length || 0) / totalVotes) * 100) : 0;
                                  return (
                                    <button key={opt.id} data-testid={`vote-${opt.id}`}
                                      onClick={(e) => { e.stopPropagation(); handleVote(card.eventId, opt.id); }}
                                      style={{ width: '100%', padding: '12px 14px', marginBottom: 8, background: voted ? '#D1FAE5' : '#F9FAFB',
                                        border: `2px solid ${voted ? TEAL_ACCENT : '#E5E7EB'}`, borderRadius: 10, textAlign: 'left', cursor: 'pointer',
                                        display: 'flex', alignItems: 'center', gap: 10, position: 'relative', overflow: 'hidden' }}>
                                      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`, background: `${TEAL_ACCENT}12`, transition: 'width 0.3s' }} />
                                      <div style={{ width: 20, height: 20, borderRadius: '50%', border: `2px solid ${voted ? TEAL_ACCENT : '#D1D5DB'}`, background: voted ? TEAL_ACCENT : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, position: 'relative' }}>
                                        {voted && <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'white' }} />}
                                      </div>
                                      <span style={{ flex: 1, fontSize: 14, fontWeight: voted ? 600 : 400, color: '#1F2937', position: 'relative' }}>{opt.text}</span>
                                      <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 500, position: 'relative' }}>{pct}%</span>
                                    </button>
                                  );
                                })}
                              </div>
                            )}

                            {/* RSVP + Completion Verification */}
                            {card.event_type !== 'voting' && (() => {
                              const hasRsvp = hasJoinedEvent(card, user?.id);
                              const vm = card.verification_method || 'manual';
                              const mySub = myEventSubmission(card, user?.id);
                              const subStatus = mySub?.status;

                              // Date gating: parse end_date and start_date
                              const endStr = card.end_date || card.event_date;
                              const startStr = card.start_date || card.event_date;
                              const endDt = endStr ? new Date(endStr.includes('T') ? endStr : endStr + 'T23:59:59Z') : null;
                              const startDt = startStr ? new Date(startStr.includes('T') ? startStr : startStr + 'T00:00:00Z') : null;
                              const isRealtime = card.is_realtime;
                              const showCompletionCTA = hasRsvp && vm !== 'manual' && (
                                (isRealtime && startDt && serverTime >= startDt) ||
                                (!isRealtime && endDt && serverTime >= endDt)
                              );
                              const isManual = vm === 'manual';

                              // Status chip styles
                              const statusChip = (status, reason) => {
                                const cfg = {
                                  pending: { bg: '#FEF3C7', color: '#92400E', text: 'Submitted \u2013 Pending Review' },
                                  approved: { bg: '#D1FAE5', color: '#047857', text: `Verified \u2713${card.reward_stars ? ` \u00B7 +${card.reward_stars} stars` : ''}` },
                                  rejected: { bg: '#FEE2E2', color: '#B91C1C', text: 'Not Approved' },
                                }[status];
                                if (!cfg) return null;
                                return (
                                  <div style={{ marginBottom: 8 }}>
                                    <div data-testid={`status-${status}`} style={{ fontSize: 12, padding: '6px 10px', borderRadius: 6, background: cfg.bg, color: cfg.color, fontWeight: 600 }}>
                                      {cfg.text}
                                    </div>
                                    {status === 'rejected' && reason && (
                                      <div style={{ fontSize: 11, color: '#B91C1C', marginTop: 4, padding: '0 2px' }}>Reason: {reason}</div>
                                    )}
                                  </div>
                                );
                              };

                              return (
                                <div>
                                  {/* RSVP row */}
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                                    <span style={{ fontSize: 12, color: '#9CA3AF' }}>{eventAttendeeCount(card)} attending</span>
                                    {endDt && serverTime > endDt ? (
                                      <span style={{ padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, background: '#F1F5F9', color: '#94A3B8' }}>Ended</span>
                                    ) : (
                                      <button data-testid={`rsvp-${card.eventId}`} onClick={(e) => { e.stopPropagation(); handleRsvp(card.eventId); }}
                                        style={{ padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                                          background: hasRsvp ? '#E0F2F1' : TEAL_ACCENT, color: hasRsvp ? TEAL_ACCENT : '#fff',
                                          border: hasRsvp ? `1px solid ${TEAL_ACCENT}` : 'none' }}>
                                        {hasRsvp ? 'Cancel RSVP' : 'RSVP'}
                                      </button>
                                    )}
                                  </div>
                                  {hasRsvp && !(endDt && serverTime > endDt) && (
                                    <div style={{ marginTop: 8 }}>
                                      <a
                                        data-testid={`add-cal-${card.eventId}`}
                                        href={(() => {
                                          const fmt = (d) => d ? new Date(d).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '') : '';
                                          const start = fmt(card.start_date || card.event_date);
                                          const end = fmt(card.end_date || card.start_date || card.event_date);
                                          const loc = card.eventMode === 'online' ? (card.meetLink || '') : (card.location || '');
                                          return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(card.title)}&dates=${start}/${end}&details=${encodeURIComponent(card.description || '')}&location=${encodeURIComponent(loc)}`;
                                        })()}
                                        target="_blank" rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, width: '100%', padding: '8px 0', borderRadius: 8, border: '1px solid #E5E7EB', background: '#FAFAFA', fontSize: 12, fontWeight: 600, color: '#374151', textDecoration: 'none', cursor: 'pointer' }}>
                                        <Calendar size={14} color="#6B7280" /> Add to Calendar
                                      </a>
                                    </div>
                                  )}

                                  {/* Post-RSVP: Completion verification area */}
                                  {hasRsvp && (
                                    <div style={{ borderTop: '1px solid #F3F4F6', paddingTop: 10 }}>
                                      {/* Status chip if submitted (skip approved — shown above image) */}
                                      {mySub && subStatus !== 'approved' && statusChip(subStatus, mySub.rejection_reason)}

                                      {/* If event hasn't reached CTA date yet, show countdown */}
                                      {!showCompletionCTA && vm !== 'manual' && !mySub && (
                                        <div style={{ fontSize: 12, color: '#6B7280', textAlign: 'center', padding: '8px 0' }}>
                                          Verification opens {isRealtime ? 'on' : 'after'} {new Date(isRealtime ? startStr : endStr).toLocaleDateString()}
                                        </div>
                                      )}

                                      {/* Manual event: admin marks */}
                                      {isManual && !mySub && (
                                        <div style={{ fontSize: 12, color: '#6B7280', textAlign: 'center', padding: '8px 0', background: '#F9FAFB', borderRadius: 8 }}>
                                          Attendance will be marked by the organiser
                                        </div>
                                      )}

                                      {/* Show CTAs only when date allows AND not already approved */}
                                      {showCompletionCTA && subStatus !== 'approved' && !mySub?.status?.startsWith('pending') && (
                                        <div>
                                          <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Verify Completion:</div>
                                          <div style={{ display: 'flex', flexDirection: (vm === 'both') ? 'column' : 'row', gap: 8 }}>
                                            {(vm === 'photo' || vm === 'both') && (
                                              <button data-testid={`photo-confirm-${card.eventId}`} onClick={(e) => { e.stopPropagation(); handlePhotoConfirm(card.eventId); }}
                                                style={{ flex: 1, padding: '10px', borderRadius: 8, border: `1.5px solid ${TEAL_ACCENT}`,
                                                  background: '#E0F2F1', color: TEAL_ACCENT, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                                                <Camera size={14} /> Post Completion Photo
                                              </button>
                                            )}
                                            {(vm === 'geotag' || vm === 'both') && (
                                              <button data-testid={`geo-confirm-${card.eventId}`} onClick={(e) => { e.stopPropagation(); handleGeoConfirm(card.eventId); }}
                                                style={{ flex: 1, padding: '10px', borderRadius: 8, border: `1.5px solid ${TEAL_ACCENT}`,
                                                  background: '#E0F2F1', color: TEAL_ACCENT, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                                                <Navigation size={14} /> Mark Attendance
                                              </button>
                                            )}
                                          </div>
                                        </div>
                                      )}

                                      {/* Allow resubmission if rejected */}
                                      {showCompletionCTA && subStatus === 'rejected' && (
                                        <div style={{ marginTop: 8 }}>
                                          <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Resubmit:</div>
                                          <div style={{ display: 'flex', flexDirection: (vm === 'both') ? 'column' : 'row', gap: 8 }}>
                                            {(vm === 'photo' || vm === 'both') && (
                                              <button data-testid={`photo-resubmit-${card.eventId}`} onClick={(e) => { e.stopPropagation(); handlePhotoConfirm(card.eventId); }}
                                                style={{ flex: 1, padding: '10px', borderRadius: 8, border: `1.5px solid #F59E0B`,
                                                  background: '#FFFBEB', color: '#B45309', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                                                <Camera size={14} /> Resubmit Photo
                                              </button>
                                            )}
                                            {(vm === 'geotag' || vm === 'both') && (
                                              <button data-testid={`geo-resubmit-${card.eventId}`} onClick={(e) => { e.stopPropagation(); handleGeoConfirm(card.eventId); }}
                                                style={{ flex: 1, padding: '10px', borderRadius: 8, border: `1.5px solid #F59E0B`,
                                                  background: '#FFFBEB', color: '#B45309', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                                                <Navigation size={14} /> Resubmit Location
                                              </button>
                                            )}
                                          </div>
                                        </div>
                                      )}

                                      {/* Stars info */}
                                      {card.reward_stars > 0 && subStatus !== 'approved' && (
                                        <div style={{ fontSize: 11, color: '#9CA3AF', textAlign: 'center', marginTop: 8 }}>
                                          {card.reward_stars} stars on admin approval
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        )}
                      </div>

                      {/* Footer */}
                      <div style={{ padding: '12px 16px', borderTop: '1px solid rgba(0,0,0,0.04)', background: '#FAFBFC', borderBottomLeftRadius: 20, borderBottomRightRadius: 20 }}>
                        {card.type === 'user_post' && (
                          <div style={{ display: 'flex', justifyContent: 'space-around' }} data-testid="user-post-actions">
                            <button data-testid="post-like-btn" onClick={(e) => { e.stopPropagation(); handleLikePost(card.postId); }}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: likedPosts.has(card.postId) ? '#EF4444' : '#9CA3AF', padding: '6px 12px', borderRadius: 6 }}>
                              <Heart size={22} fill={likedPosts.has(card.postId) ? '#EF4444' : 'none'} />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); handleShare(card); }}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: '8px 16px', borderRadius: 8 }}>
                              <Share2 size={22} />
                            </button>
                          </div>
                        )}
                        {card.type === 'event' && (
                          <div style={{ textAlign: 'center', fontSize: 12, color: '#6B7280', padding: '4px 0' }}>
                            {card.event_type === 'voting'
                              ? `${card.options?.reduce((s, o) => s + (o.votes?.length || 0), 0) || 0} votes`
                              : `${eventAttendeeCount(card)} attending`}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Empty state */}
              {feedCards.length === 0 && (
                <div style={{ flexShrink: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ background: '#FFFFFF', borderRadius: 20, padding: 40, textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                    <Star size={48} color="#D1D5DB" style={{ marginBottom: 16 }} />
                    <p style={{ fontSize: 16, color: '#6B7280', fontWeight: 500 }}>No posts or events yet</p>
                    <p style={{ fontSize: 13, color: '#9CA3AF' }}>Be the first to share something!</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

      </div>

      {/* Delete Confirm Modal */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setDeleteConfirm(null)}
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              style={{ background: '#fff', borderRadius: 16, padding: 24, maxWidth: 300, width: '90%', textAlign: 'center' }}>
              <p style={{ fontSize: 15, fontWeight: 600, color: '#1F2937', marginBottom: 8 }}>Delete this post?</p>
              <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 20 }}>This action cannot be undone.</p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setDeleteConfirm(null)}
                  style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1px solid #E5E7EB', background: '#F9FAFB', color: '#374151', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>Cancel</button>
                <button onClick={() => handleDeletePost(deleteConfirm.id)}
                  style={{ flex: 1, padding: '10px', borderRadius: 10, border: 'none', background: '#EF4444', color: '#fff', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>Delete</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <WallModal showWallModal={showWallModal} wallData={wallData} wallLoading={false}
        wallMessage="" setWallMessage={() => {}}
        onClose={() => { setShowWallModal(null); setWallData(null); }}
        onSendMessage={() => {}} onReactToMessage={() => {}} user={user} />

      {activeAction && (
        <CommunityActionModal
          action={activeAction}
          onClose={() => setActiveAction(null)}
          onResponded={(resp) => {
            // Patch the local list so the +5 ⭐ badge disappears and the next
            // tap immediately renders the locked/result state without a refetch.
            const patch = (arr) => arr.map(it => it.id === activeAction.id ? { ...it, my_response: resp } : it);
            if (activeAction.action_type === 'quiz') setActionQuizzes(patch);
            if (activeAction.action_type === 'poll') setActionPolls(patch);
            setActiveAction(prev => prev ? { ...prev, my_response: resp, tally: resp.tally, total_votes: resp.total_votes } : prev);
          }}
        />
      )}

      <CommunityBottomNav active="home" />
    </div>
  );
};

export default CommunityHomePage;
